// frontend/src/components/boarding/ManualBoardingModal.js
import React, { useState } from 'react';
import { X, UserPlus, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import boardingService from '../../services/boardingService';
import notificationSound from '../../utils/notificationSound';

/**
 * ManualBoardingModal
 *
 * Reusable modal for boarding passengers with rider/luggage override capability
 * Supports three modes:
 * 1. Manual boarding (no QR code) - fallback method for technical issues
 * 2. QR scan confirmation - allows override after successful QR scan
 * 3. Edit mode - edit counts for already boarded passengers
 *
 * Use cases: Phone battery dead, QR code not working, verify/adjust counts after scan, correct mistakes
 * Records location and allows partial boarding adjustments
 */
const ManualBoardingModal = ({
  open,
  onClose,
  booking,
  tripId,
  entityId,
  onSuccess,
  mode = 'manual', // 'manual', 'qr_confirm', or 'edit'
  qrData = null // QR code data (only used in qr_confirm mode)
}) => {
  const { t } = useTranslation();
  const { background, text, cn } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ridersBoarded, setRidersBoarded] = useState(booking?.bookingRiders || 1);
  const [luggageLoaded, setLuggageLoaded] = useState(booking?.bookingLuggages || 0);
  const [notes, setNotes] = useState('');

  // Track if field is being edited (to allow empty state during typing)
  const [ridersInputValue, setRidersInputValue] = useState(String(booking?.bookingRiders || 1));
  const [luggageInputValue, setLuggageInputValue] = useState(String(booking?.bookingLuggages || 0));

  // Track original values for change detection (only in edit mode)
  const originalRiders = booking?.bookingRiders || 1;
  const originalLuggage = booking?.bookingLuggages || 0;

  // Check if any changes have been made (only relevant in edit mode)
  const hasChanges = mode === 'edit'
    ? ridersBoarded !== originalRiders || luggageLoaded !== originalLuggage || notes.trim() !== ''
    : true; // Always allow submission in manual/qr_confirm modes

  /**
   * Handle boarding submission
   * Routes to appropriate method based on mode (manual vs QR scan confirmation)
   */
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get current location
      const location = await boardingService.getCurrentLocation();

      let response;

      if (mode === 'edit') {
        // Edit mode - update boarding counts for already boarded passenger
        console.log('Updating boarding counts:', {
          entityId,
          tripId,
          bookingId: booking.bookingId,
          ridersBoarded,
          luggageLoaded,
          notes: notes || null
        });

        response = await boardingService.updateBoardingCounts(
          entityId,
          tripId,
          booking.bookingId,
          ridersBoarded,
          luggageLoaded,
          notes || null
        );
      } else if (mode === 'qr_confirm' && qrData) {
        // QR scan confirmation mode - use scanBoardingPass method
        response = await boardingService.scanBoardingPass(
          entityId,
          tripId,
          qrData.code,
          ridersBoarded,
          luggageLoaded,
          location
        );
      } else {
        // Manual boarding mode - use manualBoardPassenger method
        response = await boardingService.manualBoardPassenger(
          entityId,
          tripId,
          booking.bookingId,
          ridersBoarded,
          luggageLoaded,
          location,
          notes || null
        );
      }

      if (response.success) {
        // Success - play success sound
        notificationSound.play('success');
        if (onSuccess) {
          onSuccess(response.data);
        }
        onClose();
      } else {
        notificationSound.play('error');
        setError(response.error || 'Failed to board passenger');
      }
    } catch (err) {
      console.error('Boarding error:', err);

      const errorCode = err.response?.data?.code;
      const errorMessage = err.response?.data?.error;
      let displayMessage;

      // Handle different error scenarios based on mode
      if (mode === 'edit') {
        if (errorCode === 'NOT_BOARDED') {
          displayMessage = t('canOnlyEditBoardedPassengers') || 'Can only edit counts for boarded passengers';
        } else {
          displayMessage = errorMessage || t('failedToUpdateCounts') || 'Failed to update counts';
        }
      } else {
        // Handle boarding errors
        if (errorCode === 'ALREADY_BOARDED') {
          displayMessage = t('alreadyBoarded');
        } else if (errorCode === 'MARKED_AS_NO_SHOW') {
          displayMessage = t('markedAsNoShow');
        } else if (errorCode === 'BOOKING_NOT_CONFIRMED') {
          displayMessage = 'Booking not confirmed';
        } else if (errorCode === 'WRONG_TRIP') {
          displayMessage = t('wrongTrip');
        } else if (errorCode === 'INVALID_BOARDING_PASS') {
          displayMessage = t('invalidBoardingPass');
        } else {
          displayMessage = errorMessage || 'Failed to board passenger';
        }
      }

      notificationSound.play('error');
      setError(displayMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Validate rider count
   * Allows clearing the field and entering new values
   */
  const handleRiderCountChange = (e) => {
    const inputValue = e.target.value;

    // Always update the input value (allows empty field during typing)
    setRidersInputValue(inputValue);

    // If empty, set to 0 in the actual state
    if (inputValue === '') {
      setRidersBoarded(0);
      return;
    }

    const value = parseInt(inputValue, 10);
    // Only update state if it's a valid non-negative number
    if (!isNaN(value) && value >= 0) {
      setRidersBoarded(value);
    }
  };

  /**
   * Validate luggage count
   * Allows clearing the field and entering new values
   */
  const handleLuggageCountChange = (e) => {
    const inputValue = e.target.value;

    // Always update the input value (allows empty field during typing)
    setLuggageInputValue(inputValue);

    // If empty, set to 0 in the actual state
    if (inputValue === '') {
      setLuggageLoaded(0);
      return;
    }

    const value = parseInt(inputValue, 10);
    // Only update state if it's a valid non-negative number
    if (!isNaN(value) && value >= 0) {
      setLuggageLoaded(value);
    }
  };

  if (!open || !booking) return null;

  const isPartialBoarding = ridersBoarded < booking.bookingRiders;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className={cn("max-w-md w-full rounded-lg shadow-xl", background.secondary)}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-blue-500" />
            <h2 className={cn("text-xl font-semibold", text.primary)}>
              {mode === 'edit' ? t('editCounts') : (mode === 'qr_confirm' ? t('confirmBoarding') : t('manualBoarding'))}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* Info banner for edit mode */}
          {mode === 'edit' && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {t('editCountsInfo')}
              </p>
            </div>
          )}

          {/* Passenger Information */}
          <div className="mb-6">
            <p className={cn("text-sm font-medium mb-2", text.secondary)}>
              {t('riders')}
            </p>
            <h3 className={cn("text-lg font-semibold mb-1", text.primary)}>
              {booking.passengerName}
            </h3>
            <p className={cn("text-sm", text.secondary)}>
              {booking.pickup} → {booking.dropoff}
            </p>
          </div>

          <div className="h-px bg-gray-200 dark:border-gray-700 my-4"></div>

          {/* Riders Count Input */}
          <div className="mb-6">
            <label className={cn("block text-sm font-medium mb-2", text.primary)}>
              {t('actualRidersBoarded')}
            </label>
            <input
              type="number"
              min="0"
              max={booking.bookingRiders + 5}
              value={ridersInputValue}
              onChange={handleRiderCountChange}
              className={cn(
                "w-full px-3 py-2 rounded border",
                "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-800",
                text.primary,
                "focus:outline-none focus:ring-2 focus:ring-blue-500"
              )}
            />
            <p className={cn("text-xs mt-1", text.secondary)}>
              {mode === 'edit' ? t('currentRiders') : t('expectedRiders')}: {booking.bookingRiders}
            </p>

            {isPartialBoarding && (
              <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    {t('partialBoarding')}: {ridersBoarded} {t('of')} {booking.bookingRiders} {t('riders')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Luggage Count Input */}
          <div className="mb-6">
            <label className={cn("block text-sm font-medium mb-2", text.primary)}>
              {t('luggageLoaded')}
            </label>
            <input
              type="number"
              min="0"
              value={luggageInputValue}
              onChange={handleLuggageCountChange}
              className={cn(
                "w-full px-3 py-2 rounded border",
                "border-gray-300 dark:border-gray-600",
                "bg-white dark:bg-gray-800",
                text.primary,
                "focus:outline-none focus:ring-2 focus:ring-blue-500"
              )}
            />
            <p className={cn("text-xs mt-1", text.secondary)}>
              {mode === 'edit'
                ? `${t('currentLuggage')}: ${booking.bookingLuggages || 0}`
                : t('expectedLuggage', { count: booking.bookingLuggages || 0 })
              }
            </p>
          </div>

          {/* Notes Input (show in manual and edit modes) */}
          {(mode === 'manual' || mode === 'edit') && (
            <div className="mb-4">
              <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                {t('notes')} ({t('optional')})
              </label>
              <textarea
                rows="3"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={mode === 'edit' ? t('reasonForCorrection') : t('enterAnyNotes')}
                className={cn(
                  "w-full px-3 py-2 rounded border",
                  "border-gray-300 dark:border-gray-600",
                  "bg-white dark:bg-gray-800",
                  text.primary,
                  "focus:outline-none focus:ring-2 focus:ring-blue-500",
                  "resize-none"
                )}
              />
            </div>
          )}


        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || ridersBoarded === 0 || !hasChanges}
            className="flex items-center gap-2 px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserPlus className="h-4 w-4" />
            {loading ? t('loading') : (mode === 'edit' ? t('saveChanges') : t('confirmBoarding'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualBoardingModal;
